import React, { useEffect, useState, useRef } from "react";
import Button from "@mui/material/Button";
import moment from "moment";
import { MSpinner, MCard } from "components/Shared";
import { MCardField } from "components/Shared/MCard";
import MGridForm from "components/Shared/MGridForm";
import { MGridFormDefinition } from "components/Shared/MGridForm/MGridFormProps";
import { DATA_TYPE_STRING, DATA_TYPE_DATE_TIME_DOW, DATA_TYPE_DATE } from "typeConstants/dataTypes";
import { UnderwriterPermissionToExtendPageInformation, Date as SimpleDate } from "interfaces/Mueller.Business.Models";
import {
  getPermissionToExtendPageInfo,
  approvePermissionToExtend,
  denyPermissionToExtend,
} from "requests/dueDateExtend";

const Logo = () => {
  return (
    <div>
      <img
        className="logo"
        src="https://www2.muellerreports.com/wp-content/uploads/2016/11/Mueller-logo-web.png"
        alt="Mueller Logo"
      />
    </div>
  );
};

const emptyFormContent = {
  detail: "",
};

interface RouteParams {
  workitemid: string;
}
interface RouteMatch {
  params: RouteParams;
}
interface UWPermissionToExtendResponseProps {
  match: RouteMatch; // from router
}

const UWPermissionToExtendResponse = (props: UWPermissionToExtendResponseProps) => {
  const workitemid: string = props.match.params.workitemid;

  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [info, setInfo] = useState<UnderwriterPermissionToExtendPageInformation>(null);
  const [headerText, setHeaderText] = useState<string>("");
  const [isDenying, setIsDenying] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [denyDetailsForm, setDenyDetailsForm] = useState(emptyFormContent);

  const mounted = useRef<boolean>(true);
  const formControlMethods = useRef<any>();

  useEffect(() => {
    getPermissionToExtendPageInfo(
      workitemid,
      (resp) => {
        setInfo(resp);

        if (resp.errorMessage === null) {
          const apptDate = moment(resp.appointmentDate).format("MM/DD/YYYY");
          const newDueDate = moment(resp.extendedDueDate).format("MM/DD/YYYY");
          setHeaderText(
            `This case will be inspected on ${apptDate} and the new due date will be ${newDueDate} if no action is taken.`
          );
        }
        setIsFetching(false);
      },
      () => setIsFetching(false),
      mounted
    );

    return () => {
      mounted.current = false;
    };
  }, []);

  const submitError = () => {
    setIsFetching(false);
    setHeaderText("We're sorry, an unknown error occurred. Please refresh the page and try again.");
  };

  const onApprove = () => {
    setIsFetching(true);
    setIsSubmitted(true);
    approvePermissionToExtend(
      workitemid,
      () => {
        setIsFetching(false);
        setHeaderText("Case due date has been extended. Thank you.");
      },
      submitError,
      mounted
    );
  };

  const onDeny = () => {
    setIsFetching(true);
    setIsSubmitted(true);
    denyPermissionToExtend(
      workitemid,
      denyDetailsForm,
      () => {
        setIsFetching(false);
        setHeaderText("Case due date extension has been denied. Thank you.");
      },
      submitError,
      mounted
    );
  };

  const handleDenyFormChange = (updatedObject) => setDenyDetailsForm({ ...updatedObject });

  const getOrderInformationFields = (): MCardField[] => {
    const addField = (key: string, label: string, value: string | number | SimpleDate, dataType = undefined) => {
      if (value) {
        const field: MCardField = {
          key: key,
          label: label,
          value: value,
        };
        if (dataType) {
          field.dataType = dataType;
        }
        fields.push(field);
      }
    };
    const fields = [];
    addField("control-number", "Control #", info.controlIdNumber);
    addField("policy-number", "Policy #", info.policyNumber);
    addField("customer", "Customer", info.customerInfo);
    addField("report-type", "Report Type", info.reportType);
    addField("survey-address", "Survey Address", info.surveyAddress.displayAddress);
    addField("original-due-date", "Original Due Date", info.dueDate, DATA_TYPE_DATE);
    if (!isDenying) {
      addField("new-due-date", "New Due Date", info.extendedDueDate, DATA_TYPE_DATE);
    }
    addField("ph-name", "Policyholder Name", info.policyholderName.fullName);
    addField("ph-email", "Policyholder Email", info.email);
    addField("agent-name", "Agent Name", info.agentName);
    addField("agent-code", "Agent Code", info.agentCode);
    addField("agent-phone", "Agent Phone", info.agentPhone);
    addField("agent-email", "Agent Email", info.agentEmail);
    return fields;
  };

  // Render

  if (isFetching) return <MSpinner />;
  if (info.errorMessage !== null)
    return (
      <div className="uw-permission-response">
        <Logo />
        <div>
          <h2>{info.errorMessage}</h2>
        </div>
      </div>
    );

  const interactivePageContent = () => {
    const denyDetailsFormDefinition: MGridFormDefinition[][] = [
      [{ dataType: DATA_TYPE_STRING, label: "Enter Details Here", source: "detail" }],
    ];

    if (isDenying) {
      return (
        <>
          <div className="more-info">
            <MGridForm
              key="deny-details"
              data={denyDetailsForm}
              onChange={handleDenyFormChange}
              formDefinition={denyDetailsFormDefinition}
              getControlMethods={(cm) => {
                formControlMethods.current = cm;
              }}
              webform
            />
          </div>
          <div className="button-container">
            <Button className="button" onClick={() => setIsDenying(false)}>
              Back
            </Button>
            <Button className="button" onClick={onDeny}>
              Submit
            </Button>
          </div>
        </>
      );
    } else {
      return (
        <div className="button-container">
          <Button className="button" onClick={onApprove}>
            Approve
          </Button>
          <Button className="button" onClick={() => setIsDenying(true)}>
            Deny
          </Button>
        </div>
      );
    }
  };

  return (
    <div className="uw-permission-response">
      <Logo />
      <MCard title="Status">
        <h2>{headerText}</h2>
      </MCard>
      <MCard title="Order Information" fields={getOrderInformationFields()} allowMultipleColumns={false} />
      <MCard title="Call Log">
        {info.callLogs.map((c, i) => (
          <MCard
            title={`Call #${i + 1}`}
            allowMultipleColumns={false}
            fields={[
              { key: `Date${i}`, label: "Date", value: c.dateCreated, dataType: DATA_TYPE_DATE_TIME_DOW },
              { key: `Contact${i}`, label: "Contact", value: c.contact },
              { key: `Details${i}`, label: "Details", value: c.details },
              { key: `Notes${i}`, label: "Notes", value: c.notes },
            ]}
          />
        ))}
      </MCard>
      {!isSubmitted && interactivePageContent()}
    </div>
  );
};

export default UWPermissionToExtendResponse;
